import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import toast from 'react-hot-toast';
import { getPartnerDetail, togglePartner } from '@/services/affiliateApi';

export default function AffiliatePartnerDetail() {
  const { partnerId } = useParams<{ partnerId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['affiliate-partner-detail', partnerId],
    queryFn: () => getPartnerDetail(partnerId!),
    enabled: !!partnerId,
  });

  const toggleMutation = useMutation({
    mutationFn: () => togglePartner(partnerId!),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['affiliate-partner-detail', partnerId] });
      toast.success(result.message);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed'),
  });

  if (isLoading) return <div className="text-gray-500 p-8">Loading...</div>;
  if (error || !data) return <div className="text-red-500 p-8">Error loading partner details</div>;

  const { partner, referrals, commissions, payouts } = data;

  const statusBadgeVariant = (status: string) => {
    const map: Record<string, string> = {
      qualified: 'success', commission_active: 'info', commission_complete: 'secondary',
      churned: 'danger', pending: 'warning', approved: 'success', paid: 'success',
      completed: 'success', failed: 'danger', processing: 'warning', reversed: 'danger',
    };
    return (map[status] || 'secondary') as any;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/affiliates')}>
            &larr; Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">{partner.full_name}</h1>
          <Badge variant={partner.is_active ? 'success' : 'danger'}>
            {partner.is_active ? 'Active' : 'Suspended'}
          </Badge>
        </div>
        <Button
          variant={partner.is_active ? 'danger' : 'secondary'}
          onClick={() => toggleMutation.mutate()}
          disabled={toggleMutation.isPending}
        >
          {partner.is_active ? 'Suspend Partner' : 'Reactivate Partner'}
        </Button>
      </div>

      {/* Partner Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><h2 className="text-lg font-semibold">Partner Info</h2></CardHeader>
          <CardBody>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div><dt className="text-gray-500">Email</dt><dd>{partner.email}</dd></div>
              <div><dt className="text-gray-500">Phone</dt><dd>{partner.phone || 'N/A'}</dd></div>
              <div><dt className="text-gray-500">Organization</dt><dd>{partner.organization || 'N/A'}</dd></div>
              <div><dt className="text-gray-500">Type</dt><dd>{partner.organization_type || 'N/A'}</dd></div>
              <div><dt className="text-gray-500">Website</dt><dd>{partner.website_url || 'N/A'}</dd></div>
              <div><dt className="text-gray-500">Commission Rate</dt><dd>{(partner.commission_rate * 100).toFixed(0)}%</dd></div>
              <div><dt className="text-gray-500">Promo Code</dt><dd className="font-mono text-blue-600">{partner.promo_codes?.code || 'N/A'}</dd></div>
              <div><dt className="text-gray-500">Stripe Connect</dt><dd>
                {partner.stripe_onboarding_complete ? (
                  <Badge variant="success">Connected</Badge>
                ) : partner.stripe_connect_account_id ? (
                  <Badge variant="warning">Incomplete</Badge>
                ) : (
                  <Badge variant="secondary">Not Set Up</Badge>
                )}
              </dd></div>
            </dl>
          </CardBody>
        </Card>

        <Card>
          <CardHeader><h2 className="text-lg font-semibold">Stats</h2></CardHeader>
          <CardBody>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div><dt className="text-gray-500">Total Referrals</dt><dd className="text-xl font-bold">{partner.total_referrals}</dd></div>
              <div><dt className="text-gray-500">Qualified</dt><dd className="text-xl font-bold text-green-600">{partner.total_qualified_referrals}</dd></div>
              <div><dt className="text-gray-500">Total Earned</dt><dd className="text-xl font-bold text-green-600">${(partner.total_commission_earned_cents / 100).toFixed(2)}</dd></div>
              <div><dt className="text-gray-500">Pending Payout</dt><dd className="text-xl font-bold text-orange-600">${(partner.pending_payout_cents / 100).toFixed(2)}</dd></div>
              <div><dt className="text-gray-500">Joined</dt><dd>{new Date(partner.created_at).toLocaleDateString()}</dd></div>
              <div><dt className="text-gray-500">Code Uses</dt><dd>{partner.promo_codes?.times_redeemed || 0}</dd></div>
            </dl>
          </CardBody>
        </Card>
      </div>

      {/* Referrals */}
      <Card>
        <CardHeader><h2 className="text-lg font-semibold">Referrals ({referrals.length})</h2></CardHeader>
        <CardBody>
          {referrals.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No referrals yet</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Business</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Billing</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Months Left</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {referrals.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-3 text-sm">{r.referred_business?.business_name || r.referred_email || 'Unknown'}</td>
                    <td className="px-4 py-3 text-sm"><Badge variant={statusBadgeVariant(r.status)}>{r.status}</Badge></td>
                    <td className="px-4 py-3 text-sm">{r.billing_cycle || '—'}</td>
                    <td className="px-4 py-3 text-sm">{r.commission_months_remaining}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{new Date(r.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {/* Commissions */}
      <Card>
        <CardHeader><h2 className="text-lg font-semibold">Commissions ({commissions.length})</h2></CardHeader>
        <CardBody>
          {commissions.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No commissions yet</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Base Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {commissions.map((c) => (
                  <tr key={c.id}>
                    <td className="px-4 py-3 text-sm">${(c.base_amount_cents / 100).toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm">{(c.commission_rate * 100).toFixed(0)}%</td>
                    <td className="px-4 py-3 text-sm font-medium text-green-600">${(c.commission_amount_cents / 100).toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm">{c.month_number ?? 'Annual'}</td>
                    <td className="px-4 py-3 text-sm"><Badge variant={statusBadgeVariant(c.status)}>{c.status}</Badge></td>
                    <td className="px-4 py-3 text-sm text-gray-500">{new Date(c.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {/* Payouts */}
      <Card>
        <CardHeader><h2 className="text-lg font-semibold">Payouts ({payouts.length})</h2></CardHeader>
        <CardBody>
          {payouts.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No payouts yet</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transfer ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payouts.map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-3 text-sm font-medium">${(p.amount_cents / 100).toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm font-mono text-xs text-gray-500">{p.stripe_transfer_id || '—'}</td>
                    <td className="px-4 py-3 text-sm"><Badge variant={statusBadgeVariant(p.status)}>{p.status}</Badge></td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {p.processed_at ? new Date(p.processed_at).toLocaleDateString() : new Date(p.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
